import { SYNC_POLL_MS } from "./schema";

function getSupabaseHealthUrl() {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!baseUrl) return null;
  return `${baseUrl.replace(/\/$/, "")}/auth/v1/health`;
}

// Timeout más holgado (8s): en redes lentas / corporativas el probe tardaba
// más de 5s y se abortaba, dando un falso "sin conexión" aunque las consultas
// reales (que no tienen ese corte) sí funcionaban.
export async function probeSupabaseConnectivity(timeoutMs = 8000) {
  const url = getSupabaseHealthUrl();
  if (!url || typeof fetch !== "function") return false;

  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: "GET",
      signal: controller.signal,
      cache: "no-store",
      // Mandamos la apikey (pública, la misma del cliente) para que el endpoint
      // devuelva 200 en vez de 401. El 401 sin key era inofensivo (la app lo
      // trataba como "conectado"), pero ensuciaba la consola con un error rojo
      // que confundía. Cualquier respuesta HTTP (incluso 4xx) = servidor
      // accesible; solo un throw (bloqueo/red) o un 5xx cuentan como caído.
      headers: anonKey ? { apikey: anonKey } : undefined,
    });
    return response.ok || response.status < 500;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timer);
  }
}

export function getNavigatorOnline() {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine;
}

// Cuántos probes fallidos SEGUIDOS hacen falta para declarar "sin conexión".
// Con SYNC_POLL_MS=30s, exigir 2 seguidos = ~60s de fallo real antes de alarmar.
// Evita el falso "sin conexión" que veía algún admin cuando un único probe se
// bloqueaba/demoraba (extensión, antivirus, proxy o un bache momentáneo de red)
// aunque la app seguía guardando sin problema.
const FAILURE_THRESHOLD = 2;

export function createConnectivityMonitor(onChange) {
  let cancelled = false;
  let timerId = null;
  let consecutiveFailures = 0;

  const emit = async () => {
    if (cancelled) return;
    const browserOnline = getNavigatorOnline();
    const probeOk = browserOnline ? await probeSupabaseConnectivity() : false;
    if (cancelled) return;

    if (probeOk) {
      consecutiveFailures = 0;
    } else {
      consecutiveFailures += 1;
    }

    // Debounce: un fallo aislado NO marca offline. Solo declaramos "Supabase no
    // responde" tras FAILURE_THRESHOLD fallos consecutivos. Si el navegador está
    // sin red (navigator.onLine=false) sí marcamos offline en el acto.
    const supabaseReachable =
      browserOnline && (probeOk || consecutiveFailures < FAILURE_THRESHOLD);

    onChange({
      browserOnline,
      supabaseReachable,
      online: browserOnline && supabaseReachable,
      checkedAt: new Date().toISOString(),
    });
  };

  const schedule = () => {
    if (cancelled) return;
    window.clearTimeout(timerId);
    timerId = window.setTimeout(async () => {
      await emit();
      schedule();
    }, SYNC_POLL_MS);
  };

  const start = async () => {
    if (typeof window === "undefined") return () => {};

    const onOnline = async () => {
      await emit();
    };

    const onOffline = () => {
      onChange({
        browserOnline: false,
        supabaseReachable: false,
        online: false,
        checkedAt: new Date().toISOString(),
      });
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    await emit();
    schedule();

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  };

  return { start };
}

